import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { groupBy } from 'lodash';
import { BModal } from 'bootstrap-vue';
import { Mutation, State } from 'vuex-class';

import * as optionSetsApi from '@/api/shopify/optionSets';
import { ApiUser } from '@/api/shopify/user';

import { OptionSetDto } from '@extTypes/options/optionSet';
import { StoreType } from '@extTypes/api/customily/user';
import { CustomilyTemplateDto } from '@extTypes/api/customily/template';
import { AuditEntity } from '@extTypes/auditLog/types';
import { LinkedOptionSet } from '@extTypes/enums/option-sets';
import { EntityVersionRestored } from '@extTypes/versioning';

import config from '@/config/config';

import * as storeUtil from '@/utils/stores';
import { DateFormat, formatDate } from '@/utils/dates';
import { linkedOptionSets } from '@/utils/constants/option-sets';

import { FieldType, TableField } from '@/typings/dynamicTable';

import Loader from '@/components/Loader/Loader.vue';
import CustomilyProductsList from '@/components/CustomilyProductsList/CustomilyProductsList.vue';
import AuditLog from '@/components/AuditLog/AuditLog.vue';
import SetProgression from '@/components/Set/SetProgression/SetProgression.vue';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import CustomCheckbox from '@/components/shared/CustomCheckbox.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import DynamicTable from '@/components/shared/DynamicTable.vue';
import CInput from '@/components/shared/CInput.vue';
import OptionSetLinkedToProductsModal from './OptionSetLinkedToProductsModal/OptionSetLinkedToProductsModal.vue';

@Component({
    name: 'Sets',
    components: {
        Loader,
        CustomilyProductsList,
        SetProgression,
        SvgIcon,
        AuditLog,
        CustomCheckbox,
        CButton,
        CInput,
        DynamicTable,
        OptionSetLinkedToProductsModal
    }
})
export default class Set extends Vue {
    @State('user') user!: ApiUser;
    @State('store') store!: string;
    @State('forDesignTemplatesMode') forDesignTemplatesMode!: boolean;
    @Mutation('setDesignTemplatesMode') setDesignTemplatesMode!: (enabled: boolean) => void;

    selectedOptionSet: OptionSetDto | null = null;
    showOptionSetLinkedToProductsModal = false;
    searchString = '';
    totalSetsCount = 10;
    setsPerPage = 10;
    currentPage = 1;
    showLoader = false;
    setAboutToBeDeleted?: OptionSetDto | null = null;
    selectedSet?: OptionSetDto | null = null;
    entityType = AuditEntity.OPTION_SET;
    optionSets: OptionSetDto[] = [];
    productsUsingDeletedSet: {
        title: string;
        id: string;
        isDesign: boolean;
    }[] = [];
    productsUsingDeletedSets: {
        setId: string;
        setName: string;
        products: {
            title: string;
            id: string;
            isDesign: boolean;
        }[];
    }[] = [];
    optionSetsExportUrls: string[] = [];
    optionSetsZipFile: File | null = null;
    loaderMessage = 'Loading sets';
    exportingSets = false;

    selectedSets: OptionSetDto[] = [];
    pageLoaded = false;

    isManaged = false;
    showCloneButtun = false;
    showAuditLogModal = false;
    setTaskIdentifier = '';

    sortMode: 'ASC' | 'DESC' = 'DESC';
    sortBy = 'updated_at';
    isAllChecked = false;

    copiedId = '';

    tableConfig: TableField[] = [
        {
            key: 'check',
            label: '',
            thStyle: { width: '48px', padding: '0 0.75rem 0.75rem 0.75rem' }
        },
        {
            key: 'title',
            label: 'Option set',
            type: FieldType.Sort,
            sortKey: 'optionset_name',
            thStyle: { padding: '0 0.75rem 0.75rem 0.75rem' }
        },
        {
            key: 'created_at',
            label: 'Created',
            formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
            thStyle: { width: '180px' },
            type: FieldType.Sort,
            sortKey: 'created_at'
        },
        {
            key: 'updated_at',
            label: 'Last Modified',
            formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
            thStyle: { width: '180px', padding: '0 0.75rem 0.75rem 0.75rem' },
            type: FieldType.Sort,
            sortKey: 'updated_at'
        },
        {
            key: 'linked',
            label: 'Linked to Product',
            thStyle: { width: '145px', padding: '0 0.75rem 0.75rem 0.75rem' }
        },
        { key: 'actions', label: '', thStyle: { width: '250px', padding: '0 0.75rem 0.75rem 0.75rem' } }
    ];

    linkedOptionSets = linkedOptionSets;
    onlyLinkedOptionSets = LinkedOptionSet.All;
    optionSetsFiltersLabel = 'All option sets';

    // When the pagination page changes, load the new products
    @Watch('currentPage', { immediate: true })
    onPaginationChange() {
        if (this.pageLoaded) {
            localStorage.setItem('customily-current-sets-page', this.currentPage.toString());
            this.loadSets();
        }
    }

    @Watch('searchString')
    onSearchStringChange() {
        this.searchSets();
    }

    @Watch('sortBy')
    onSortChange() {
        this.searchSets();
    }

    @Watch('sortMode')
    onSortModeChange() {
        this.searchSets();
    }

    $refs!: {
        removeSetModal: BModal;
        setInUseModal: BModal;
        uploadFileModal: BModal;
        templatePickerModal: BModal;
        optionSetsBulkDeleteModal: BModal;
        optionSetsLinkedToProductsModal: BModal;
    };

    setLoadingSetsLoaderMessage() {
        this.loaderMessage = 'Loading sets';
    }

    setExportingSetsLoaderMessage() {
        this.loaderMessage = 'Exporting sets';
    }

    async searchSets() {
        this.currentPage = 1;
        await this.getSetsCount();
        await this.loadSets();
    }

    async getSetsCount() {
        try {
            const result = await optionSetsApi.getOptionSetsCount(
                this.searchString,
                this.forDesignTemplatesMode,
                this.onlyLinkedOptionSets
            );
            this.totalSetsCount = result.count;
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    showOptionSetsBulkDeleteDialog() {
        this.$refs.optionSetsBulkDeleteModal.show();
    }

    closeOptionSetsBulkDeleteDialog() {
        this.$refs.optionSetsBulkDeleteModal.hide();
    }

    showSetDeleteDialog(setToDelete: OptionSetDto) {
        this.setAboutToBeDeleted = setToDelete;
        this.$refs.removeSetModal.show();
    }

    closeSetDeleteModal() {
        this.setAboutToBeDeleted = null;
        this.$refs.removeSetModal.hide();
    }

    async handleOptionSetsBulkDelete() {
        try {
            this.$refs.optionSetsBulkDeleteModal.hide();
            this.showLoader = true;

            const linkedSets = this.selectedSets.filter((set) => set.linked);
            this.productsUsingDeletedSets = await Promise.all(
                linkedSets.map(async (set) => {
                    const products = await optionSetsApi.getProductsWithSet(set.id);
                    return {
                        setId: set.id,
                        setName: set.title,
                        products
                    };
                })
            );
            const unlinkedOptionsSets = this.selectedSets.filter((set) => !set.linked).map((set) => set.id);

            if (unlinkedOptionsSets.length) {
                optionSetsApi
                    .bulkDeleteOptionsSets(unlinkedOptionsSets)
                    .then(async () => {
                        await this.getSetsCount();
                        await this.loadSets();
                        this.selectedSets = this.selectedSets.filter((set) => set.linked);
                    })
                    .catch((error) => {
                        this.showLoader = false;
                        window.captureException(error as Error);
                    });
            } else {
                this.showLoader = false;
                this.$refs.optionSetsLinkedToProductsModal.show();
            }
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    async removeSet(setToDelete: OptionSetDto | null | undefined) {
        try {
            if (setToDelete) {
                this.$refs.removeSetModal.hide();
                this.showLoader = true;
                this.productsUsingDeletedSet = await optionSetsApi.getProductsWithSet(setToDelete.id);

                // Don't remove set if it's in use
                if (!this.productsUsingDeletedSet.length) {
                    optionSetsApi
                        .deleteSet(setToDelete)
                        .then(async () => {
                            await this.getSetsCount();
                            await this.loadSets();
                        })
                        .catch((error) => {
                            this.showLoader = false;
                            window.captureException(error as Error);
                        });
                } else {
                    this.showLoader = false;
                    this.$refs.setInUseModal.show();
                }
            }
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    cloneOptionSet(set: OptionSetDto) {
        this.showLoader = true;
        optionSetsApi
            .cloneOptionSet(set, this.forDesignTemplatesMode)
            .then(async () => {
                await this.getSetsCount();
                await this.loadSets();
            })
            .catch((error) => {
                this.showLoader = false;
                window.captureException(error as Error);
            });
    }

    auditOptionSet(set: OptionSetDto) {
        this.selectedSet = set;
        this.showAuditLogModal = true;
    }

    onAuditClose() {
        this.selectedSet = null;
        this.showAuditLogModal = false;
    }

    addOptionSet() {
        try {
            this.$router.push({ name: 'CreateOptionSet' });
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    editOptionSet(setId: number | string) {
        try {
            this.$router.push({
                name: 'Set',
                query: {
                    set: setId.toString()
                }
            });
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    handleClickRow(set: OptionSetDto) {
        if (!this.exportingSets) {
            this.editOptionSet(set.id as unknown as number);
        }
    }

    openInNewTab(event: MouseEvent, setId: string) {
        event.stopPropagation();
        try {
            window.open(`${config.CUSTOMILY_BASE_URL}/store/set/?set=${setId}`);
        } catch (error) {
            window.captureException(error as Error);
        }
    }

    sortSets(str?: string) {
        if (str) {
            this.sortBy = str;
            this.sortMode = 'DESC';
        }
    }

    sortModeSets(event: MouseEvent) {
        event.stopPropagation();
        this.sortMode = this.sortMode === 'DESC' ? 'ASC' : 'DESC';
    }

    copyId(event: MouseEvent, id: string) {
        event.stopPropagation();

        navigator.clipboard
            .writeText(id.toString())
            .then(() => {
                this.copiedId = id;
                setTimeout(() => {
                    this.copiedId = '';
                }, 1000);
            })
            .catch((error) => {
                console.error('Error trying to copy id', error);
            });
    }

    async loadSets() {
        try {
            this.showLoader = true;
            this.optionSets = await optionSetsApi.getOptionSets(
                this.searchString,
                (this.currentPage - 1) * this.setsPerPage,
                this.setsPerPage,
                this.forDesignTemplatesMode,
                this.onlyLinkedOptionSets,
                undefined,
                this.sortBy,
                this.sortMode
            );
            this.showLoader = false;
        } catch (error) {
            this.showLoader = false;
            window.captureException(error as Error);
        }
    }

    async created() {
        const storedPage = localStorage.getItem('customily-current-sets-page');
        this.isManaged = this.user.customilyUser.IsManaged;
        this.showCloneButtun =
            !this.isManaged ||
            !this.user.customilyUser.ParentAccessRoles ||
            !this.user.customilyUser.ParentAccessRoles.split(',').includes('restricts-children');

        if (this.user?.customilyUser.AccessRoles.Serialized.includes('admin')) {
            this.setDesignTemplatesMode(!!this.$route.query['forDesignTemplates']);
        }

        if (storedPage) {
            try {
                this.currentPage = Number.parseInt(storedPage) || 1;
            } catch {
                // In case of failure just set the first page
                this.currentPage = 1;
            }
        }

        this.loadLastSessionOptionSetsFilters();
        await this.getSetsCount();
        await this.loadSets();

        window.addEventListener('message', async (event) => {
            if (event.data.action === 'refresh-sets-list') {
                await this.getSetsCount();
                await this.loadSets();
            }
        });

        this.pageLoaded = true;
    }

    showtemplateSelectionModal() {
        this.$refs.templatePickerModal.show();
    }

    onTemplateSelect(data: { template: CustomilyTemplateDto }) {
        optionSetsApi
            .createSetFromTemplate(
                data.template.id.trim().toLowerCase(),
                this.user.customilyUser.IsManaged,
                data.template.name + ' (option set)',
                '',
                this.store
            )
            .then((result) => {
                this.setTaskIdentifier = result.taskIdentifier;
            });
    }

    onTemplatePickerHidden() {
        this.setTaskIdentifier = '';
        this.$router.go(0);
    }

    showUploadFileModal() {
        this.$refs.uploadFileModal.show();
    }

    closeUploadFileModal() {
        this.$refs.uploadFileModal.hide();
    }

    isSetIdSelected(setId: string) {
        return this.selectedSets.some((s) => s.id === setId);
    }

    toggleSetSelection(set: OptionSetDto) {
        if (this.isSetIdSelected(set.id)) {
            // Remove selected set
            const index = this.selectedSets.indexOf(set);
            this.selectedSets.splice(index, 1);
        } else {
            // Add selected set
            this.selectedSets.push(set);
        }
        this.isAllChecked = this.selectedSets.length >= 1;
    }

    checkAll() {
        if (this.isAllChecked) {
            this.selectedSets = [];
        } else {
            this.selectedSets = [...this.optionSets];
        }
        this.isAllChecked = this.selectedSets.length >= 1;
    }

    get isCustomStore() {
        return storeUtil.isCustomStore(this.store, this.user.customilyUser.Stores);
    }

    getSelectedSetIdsStr(): string {
        if (this.selectedSets.length > 0) {
            return this.selectedSets.map((s) => s.id).join('_');
        }
        return '';
    }

    enterExportMode() {
        this.exportingSets = true;
    }

    exitExportMode() {
        this.exportingSets = false;
    }

    async exportOptionSets() {
        try {
            this.setExportingSetsLoaderMessage();
            this.showLoader = true;

            // Group option sets by store
            const shopGroups = groupBy(this.selectedSets, (element) => {
                return element.shop;
            });

            const tasks = [];
            for (const [shop, sets] of Object.entries(shopGroups)) {
                if (sets.length > 0) {
                    tasks.push(optionSetsApi.exportOptionSets(shop, sets.map((s) => s.id).join('_')));
                }
            }

            const responses = await Promise.all(tasks);
            this.optionSetsExportUrls = responses.map((r) => r.exportUrl);

            this.showLoader = false;
            this.exitExportMode();
            this.downloadOptionSetZips();
        } catch (error) {
            this.showLoader = false;
            window.captureException(error as Error);
        } finally {
            this.setLoadingSetsLoaderMessage();
        }
    }

    downloadOptionSetZips() {
        if (this.optionSetsExportUrls.length) {
            const urls = this.optionSetsExportUrls;

            const downloadNext = (index: number) => {
                if (index >= urls.length) {
                    return;
                }

                const a = document.createElement('a');
                a.href = urls[index];
                a.target = '_parent';

                // Add a to the doc for click to work
                (document.body || document.documentElement).appendChild(a);
                a.click();

                // Delete the temporary link
                a.parentNode?.removeChild(a);

                // Download the next file with a small timeout
                setTimeout(function () {
                    downloadNext(index + 1);
                }, 500);
            };

            // Initiate the first download.
            downloadNext(0);
        }
    }

    async importOptionSets() {
        try {
            const store = this.user.customilyUser.Stores.sort((a, b) => {
                return a.Id - b.Id;
            }).find((s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce);

            if (store && this.optionSetsZipFile) {
                this.$refs.uploadFileModal.hide();
                this.showLoader = true;
                await optionSetsApi.importOptionSets(store.Url, this.optionSetsZipFile);
                this.currentPage = 1;
                this.sortMode = 'DESC';
                this.sortBy = 'updated_at';
                await this.getSetsCount();
                await this.loadSets();
            } else {
                console.error('Please select .zip file with options to import.');
            }
        } catch (error) {
            this.showLoader = false;
            window.captureException(error as Error);
        }
    }

    goBack() {
        this.$router.go(-1);
    }

    @Watch('onlyLinkedOptionSets')
    ononlyLinkedOptionSetsChange() {
        const selectedOption = this.linkedOptionSets.find((opt) => opt.value === this.onlyLinkedOptionSets);
        this.optionSetsFiltersLabel = selectedOption ? selectedOption.text : 'All option sets';
    }

    async filterOptionSets(onlyLinkedOptionSets = LinkedOptionSet.All) {
        this.onlyLinkedOptionSets = onlyLinkedOptionSets as LinkedOptionSet;
        this.saveLastSessionOptionSetsFilters();
        await this.searchSets();
    }

    loadLastSessionOptionSetsFilters() {
        const lastSessionFilters = JSON.parse(sessionStorage.getItem('lastSessionOptionSetsFilters') || '{}');
        if (lastSessionFilters.onlyLinkedOptionSets) {
            this.onlyLinkedOptionSets = lastSessionFilters.onlyLinkedOptionSets;
        }
    }

    saveLastSessionOptionSetsFilters() {
        sessionStorage.setItem(
            'lastSessionOptionSetsFilters',
            JSON.stringify({
                onlyLinkedOptionSets: this.onlyLinkedOptionSets
            })
        );
    }

    openOptionSetLinkedToProductsModal(item: OptionSetDto) {
        this.selectedOptionSet = item;
        this.showOptionSetLinkedToProductsModal = true;
    }

    closeOptionSetLinkedToProductsModal() {
        this.selectedOptionSet = null;
        this.showOptionSetLinkedToProductsModal = false;
    }

    async onOptionSetVersionRestored(restoredVersion: EntityVersionRestored) {
        const restoredOptionSet = restoredVersion.data as OptionSetDto;
        try {
            this.showLoader = true;
            const title = `${restoredOptionSet.title} (Restored)`;
            const setId = await optionSetsApi.addOptionSet(
                {
                    title,
                    options: restoredOptionSet.options
                },
                restoredOptionSet.forDesignTemplate
            );
            await optionSetsApi.saveOptionSet({
                id: setId,
                title,
                options: restoredOptionSet.options,
                sections: [],
                shop: restoredOptionSet.shop,
                linked: false,
                forDesignTemplate: restoredOptionSet.forDesignTemplate,
                clientId: restoredOptionSet.clientId,
                libraryId: restoredOptionSet.libraryId,
                createdFromTemplate: null
            });
            await this.getSetsCount();
            await this.loadSets();
        } catch (error) {
            this.showLoader = false;
            window.captureException(error as Error);
        }
    }
}
